import { useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, RefreshCw, TrendingUp, TrendingDown, ArrowRight, Bandage } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useWatch } from "../context/WatchContext";
import { fmtOdds } from "../lib/api";

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const Move = ({ label, oldV, newV, fmt }) => {
  if (oldV === newV) return null;
  const up = newV > oldV;
  return (
    <span className="inline-flex items-center gap-1 font-data text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-400">{fmt ? fmt(oldV) : oldV}</span>
      <ArrowRight className="h-3 w-3 text-slate-600" />
      <span className={up ? "text-[#10B981]" : "text-[#EF4444]"}>{fmt ? fmt(newV) : newV}</span>
      {up ? <TrendingUp className="h-3 w-3 text-[#10B981]" /> : <TrendingDown className="h-3 w-3 text-[#EF4444]" />}
    </span>
  );
};

export const AlertsPanel = ({ open, onOpenChange }) => {
  const { alerts, checkNow, checking, markRead } = useWatch();

  useEffect(() => {
    if (open) markRead();
    // eslint-disable-next-line
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="alerts-panel"
        className="bg-[#0B0F17] border-[#233248] text-white w-full sm:max-w-md overflow-y-auto p-0"
      >
        <SheetHeader className="p-5 border-b border-[#233248] glass sticky top-0 z-10">
          <SheetTitle className="font-heading flex items-center justify-between text-white">
            <span className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#00E5FF]" /> Line Movement
            </span>
            <button
              data-testid="check-movement-button"
              onClick={checkNow}
              disabled={checking}
              className="flex items-center gap-1.5 rounded-lg bg-[#00E5FF] text-[#0B0F17] text-xs font-semibold px-2.5 py-1.5 hover:bg-[#33EaFF] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0F17]"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
              {checking ? "Checking…" : "Check now"}
            </button>
          </SheetTitle>
        </SheetHeader>

        <div className="p-4 space-y-2">
          {alerts.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="h-8 w-8 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No line moves yet.</p>
              <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
                Star the <span className="text-[#00E5FF]">★</span> on any live prop to track it. The app re-checks a few
                times a day, or tap “Check now”.
              </p>
            </div>
          ) : (
            alerts.map((a, i) => (
              <motion.div
                key={a.id}
                data-testid={`alert-${a.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`rounded-xl border p-3 ${
                  a.change_type === "injury"
                    ? a.read ? "border-[#233248] bg-[#141C2B]" : "border-[#EF4444]/50 bg-[#EF4444]/5"
                    : a.read ? "border-[#233248] bg-[#141C2B]" : "border-[#00E5FF]/40 bg-[#00E5FF]/5"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold text-sm truncate flex items-center gap-1.5">
                    {a.change_type === "injury" && <Bandage className="h-3.5 w-3.5 text-[#EF4444]" />}
                    {a.player_name}
                  </span>
                  <span className="font-data text-[10px] text-slate-500">{timeAgo(a.created_at)}</span>
                </div>
                <div className="font-data text-[11px] text-slate-400 mb-1.5">
                  {a.metric} · {a.game_label || a.sport}
                </div>
                {a.change_type === "injury" ? (
                  <div className="flex flex-col gap-1">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold font-data ${a.injury_out ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
                        {a.injury_status}
                      </span>
                      <span className="text-slate-400">{a.injury_out ? "ruled out" : "status update"}</span>
                    </span>
                    {a.backup && (
                      <span className="flex items-center gap-1 font-data text-xs text-[#10B981]">
                        <ArrowRight className="h-3 w-3" /> backup: {a.backup}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <Move label="Line" oldV={a.old_line} newV={a.new_line} />
                    <Move label="Over" oldV={a.old_over} newV={a.new_over} fmt={fmtOdds} />
                    <Move label="Under" oldV={a.old_under} newV={a.new_under} fmt={fmtOdds} />
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
